import { useState, useEffect } from 'react';
import { api } from '../../services/api';

interface SyncStatus {
  connected: boolean;
  companyName: string | null;
  stats: {
    customers_synced: string;
    invoices_synced: string;
    payments_synced: string;
    expenses_synced: string;
    recent_errors: string;
    last_sync: string | null;
  };
  recentErrors: Array<{
    entity_type: string;
    entity_id: string;
    action: string;
    error_message: string;
    created_at: string;
  }>;
  unsyncedDealers: number;
  unsyncedOrders: number;
}

interface SyncLogEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  qbo_entity_id: string;
  action: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

export default function QuickBooksPage() {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [syncLog, setSyncLog] = useState<SyncLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      setLoading(true);
      const [statusData, logData] = await Promise.all([
        api.quickbooks.getStatus(),
        api.quickbooks.getSyncLog(1, 20),
      ]);
      setStatus(statusData);
      setSyncLog(logData.logs);
      setLogTotal(logData.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleConnect() {
    try {
      const data = await api.quickbooks.getConnectUrl();
      window.open(data.authUrl, '_blank', 'width=600,height=700');
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleDisconnect() {
    if (!confirm('Disconnect from QuickBooks? Automatic syncing will stop.')) return;
    try {
      await api.quickbooks.disconnect();
      await loadStatus();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleSyncCustomers() {
    setSyncing('customers');
    try {
      const result = await api.quickbooks.syncCustomers();
      alert(`Synced ${result.synced} customers, ${result.errors} errors`);
      await loadStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSyncing(null);
    }
  }

  async function handleSyncInvoices() {
    setSyncing('invoices');
    try {
      const result = await api.quickbooks.syncInvoices();
      alert(`Synced ${result.synced} invoices, ${result.errors} errors`);
      await loadStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSyncing(null);
    }
  }

  async function loadLogPage(page: number) {
    try {
      const logData = await api.quickbooks.getSyncLog(page, 20);
      setSyncLog(logData.logs);
      setLogTotal(logData.total);
      setLogPage(page);
    } catch (err: any) {
      setError(err.message);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
        Loading QuickBooks status...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>
        QuickBooks Online
      </h1>
      <p style={{ color: '#94a3b8', marginBottom: 24 }}>
        Sync dealers, invoices, and payments to QuickBooks automatically
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', border: '1px solid #dc2626', borderRadius: 8, padding: 12, marginBottom: 16, color: '#fca5a5' }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: 12, color: '#f87171', background: 'none', border: 'none', cursor: 'pointer' }}>Dismiss</button>
        </div>
      )}

      {/* Connection Card */}
      <div style={{ background: '#1e293b', borderRadius: 12, padding: 24, marginBottom: 24, border: status?.connected ? '1px solid #16a34a' : '1px solid #334155' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 12, height: 12, borderRadius: '50%',
                background: status?.connected ? '#22c55e' : '#ef4444',
                boxShadow: status?.connected ? '0 0 8px #22c55e' : '0 0 8px #ef4444',
              }} />
              <span style={{ fontSize: 18, fontWeight: 600, color: '#f1f5f9' }}>
                {status?.connected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            {status?.connected && status.companyName && (
              <p style={{ color: '#94a3b8', margin: 0 }}>
                Company: <strong style={{ color: '#e2e8f0' }}>{status.companyName}</strong>
              </p>
            )}
          </div>
          <div>
            {status?.connected ? (
              <button
                onClick={handleDisconnect}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: '1px solid #dc2626',
                  background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: 14,
                }}
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleConnect}
                style={{
                  padding: '10px 24px', borderRadius: 8, border: 'none',
                  background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                }}
              >
                Connect to QuickBooks
              </button>
            )}
          </div>
        </div>
      </div>

      {status?.connected && (
        <>
          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { label: 'Customers Synced', value: status.stats.customers_synced || '0', color: '#3b82f6' },
              { label: 'Invoices Synced', value: status.stats.invoices_synced || '0', color: '#22c55e' },
              { label: 'Payments Synced', value: status.stats.payments_synced || '0', color: '#a855f7' },
            ].map(stat => (
              <div key={stat.label} style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Unsynced + Actions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' }}>
              <h3 style={{ color: '#f1f5f9', margin: '0 0 16px', fontSize: 16 }}>Pending Sync</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 20 }}>{status.unsyncedDealers}</span>
                  <span style={{ color: '#94a3b8', marginLeft: 8 }}>dealers without QBO Customer</span>
                </div>
                <button
                  onClick={handleSyncCustomers}
                  disabled={syncing === 'customers' || status.unsyncedDealers === 0}
                  style={{
                    padding: '6px 16px', borderRadius: 6, border: 'none',
                    background: syncing === 'customers' ? '#475569' : '#2563eb',
                    color: '#fff', cursor: status.unsyncedDealers === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 13, opacity: status.unsyncedDealers === 0 ? 0.5 : 1,
                  }}
                >
                  {syncing === 'customers' ? 'Syncing...' : 'Sync All'}
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 20 }}>{status.unsyncedOrders}</span>
                  <span style={{ color: '#94a3b8', marginLeft: 8 }}>shipped orders without QBO Invoice</span>
                </div>
                <button
                  onClick={handleSyncInvoices}
                  disabled={syncing === 'invoices' || status.unsyncedOrders === 0}
                  style={{
                    padding: '6px 16px', borderRadius: 6, border: 'none',
                    background: syncing === 'invoices' ? '#475569' : '#2563eb',
                    color: '#fff', cursor: status.unsyncedOrders === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 13, opacity: status.unsyncedOrders === 0 ? 0.5 : 1,
                  }}
                >
                  {syncing === 'invoices' ? 'Syncing...' : 'Sync All'}
                </button>
              </div>
            </div>

            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' }}>
              <h3 style={{ color: '#f1f5f9', margin: '0 0 16px', fontSize: 16 }}>Auto-Sync Triggers</h3>
              <div style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.8 }}>
                <div><span style={{ color: '#22c55e' }}>&#9679;</span> Dealer approved &rarr; QBO Customer created</div>
                <div><span style={{ color: '#22c55e' }}>&#9679;</span> Order shipped &rarr; QBO Invoice created</div>
                <div><span style={{ color: '#22c55e' }}>&#9679;</span> Payment received &rarr; QBO Payment recorded</div>
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
                Processing fees entered manually from NBC monthly statements
              </div>
            </div>
          </div>

          {/* Recent Errors */}
          {status.recentErrors.length > 0 && (
            <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24, border: '1px solid #7f1d1d' }}>
              <h3 style={{ color: '#f87171', margin: '0 0 12px', fontSize: 16 }}>
                Recent Errors ({status.stats.recent_errors} in last 7 days)
              </h3>
              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                {status.recentErrors.map((err, i) => (
                  <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #1e293b', fontSize: 13 }}>
                    <span style={{ color: '#f87171', fontWeight: 600 }}>{err.entity_type}</span>
                    <span style={{ color: '#94a3b8' }}> — {err.action} — </span>
                    <span style={{ color: '#fca5a5' }}>{err.error_message}</span>
                    <span style={{ color: '#475569', marginLeft: 8 }}>
                      {new Date(err.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sync Log */}
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' }}>
            <h3 style={{ color: '#f1f5f9', margin: '0 0 12px', fontSize: 16 }}>
              Sync Log ({logTotal} total)
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>Action</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>QBO ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontWeight: 500 }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {syncLog.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{log.entity_type}</td>
                    <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{log.action}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 12,
                        background: log.status === 'success' ? '#052e16' : '#7f1d1d',
                        color: log.status === 'success' ? '#22c55e' : '#f87171',
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                      {log.qbo_entity_id || '-'}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#fca5a5', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.error_message || '-'}
                    </td>
                  </tr>
                ))}
                {syncLog.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                      No sync activity yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {logTotal > 20 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => loadLogPage(logPage - 1)}
                  disabled={logPage <= 1}
                  style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #334155', background: '#0f172a', color: '#94a3b8', cursor: logPage <= 1 ? 'not-allowed' : 'pointer' }}
                >
                  Prev
                </button>
                <span style={{ color: '#94a3b8', padding: '4px 8px' }}>
                  Page {logPage} of {Math.ceil(logTotal / 20)}
                </span>
                <button
                  onClick={() => loadLogPage(logPage + 1)}
                  disabled={logPage >= Math.ceil(logTotal / 20)}
                  style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #334155', background: '#0f172a', color: '#94a3b8', cursor: logPage >= Math.ceil(logTotal / 20) ? 'not-allowed' : 'pointer' }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {!status?.connected && (
        <div style={{ background: '#1e293b', borderRadius: 12, padding: 32, border: '1px solid #334155', textAlign: 'center' }}>
          <h2 style={{ color: '#f1f5f9', marginBottom: 12 }}>Connect QuickBooks to get started</h2>
          <p style={{ color: '#94a3b8', maxWidth: 500, margin: '0 auto 24px' }}>
            Once connected, dealers, invoices, payments, and processing fees will automatically sync to your QuickBooks Online account.
          </p>
          <button
            onClick={handleConnect}
            style={{
              padding: '12px 32px', borderRadius: 8, border: 'none',
              background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 600,
            }}
          >
            Connect to QuickBooks
          </button>
        </div>
      )}
    </div>
  );
}
